// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { maxLengthAttr, maxQtyValue } from "@bg/XDL/Utilities/inputValidation";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductQtyOrderedStyles {
    wrapper?: InjectableCss;
    inputStyles?: TextFieldPresentationProps;
}

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    changeOnBlur?: boolean;
    extendedStyles?: ProductQtyOrderedStyles;
    showAddRemoveButtons?: boolean;
    editable?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: ProductQtyOrderedStyles = {
    wrapper: {
        css: css`
            display: flex;
            max-height: 37px;
            && button {
                width: auto;
                background: ${getColor("common.white")};
                border: 0;
                border-top: 2px solid ${getColor("text")} !important;
                border-bottom: 2px solid ${getColor("text")} !important;
                background-color: ${getColor("common.white")};
                padding: 0;
                margin: 0 !important;
                &:hover {
                    background: ${getColor("text.main")};
                }
                &:not(:disabled):hover svg {
                    fill: ${getColor("common.white")};
                }
                &:focus,
                &:active {
                    box-shadow: none !important;
                    outline: none !important;
                }
                &:disabled {
                    background: ${getColor("common.white")} !important;
                    opacity: 1;

                    > * {
                        opacity: 0.4;
                    }
                }
                && span[class*="ButtonIcon-sc"] {
                    top: 0 !important;
                }
                svg {
                    fill: ${getColor("common.ui1")};
                }
            }

            button:first-child {
                border-left: 2px solid ${getColor("text")} !important;
                padding: 0 10px;
                border-radius: 0px;
                height: 36px;
            }

            button:last-child {
                padding: 0 10px;
                border-radius: 0px;
                height: 36px;
            }

            &.qty * {
                margin-top: 0 !important;
                align-items: center;
            }
            &&& input {
                max-width: 60px;
                height: 32px;
                text-align: center;
                padding: 0 !important;
                width: auto;
                border-radius: 0;
                border: none !important;
                background: ${getColor("common.white")};
            }
            &&.disabled > * {
                opacity: 1;
                min-height: 100%;
            }
        `,
    },
    inputStyles: {
        cssOverrides: {
            formField: css`
                border-top: 2px solid ${getColor("text")};
                border-bottom: 2px solid ${getColor("text")};
                flex-grow: 1;
            `,
            formInputWrapper: css`
                &&&&& {
                    margin-top: 0 !important;
                    text-align: center;
                }
                &&& input:disabled {
                    color: black;
                    opacity: 1;
                }

                margin-left: 0 !important;
                margin-right: 0 !important;
            `,
            inputSelect: css`
                text-align: center;
                max-width: 100%;
                width: 100%;
            `,
        },
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, detail, minimumOrderQty = 1, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    changeOnBlur,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showAddRemoveButtons = true,
    editable = true,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered?.toString() || "1");
    const regexp = /^[0-9\b]+$/;

    let multipleSaleQty = minimumOrderQty || 1;
    if (
        detail &&
        (detail as any).roundingRule &&
        (detail as any).roundingRule.toLowerCase() === "multiple only" &&
        (detail as any).multipleSaleQty
    ) {
        multipleSaleQty = detail.multipleSaleQty;
    } else if (properties?.roundingRule?.toLowerCase() === "multiple only" && properties?.multipleSaleQty) {
        multipleSaleQty = parseInt(properties.multipleSaleQty, 10);
    }

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    useEffect(() => {
        if (qtyOrdered === Number(productQty) || !isValidNumberic(qtyOrdered?.toString())) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            event.stopPropagation();
            if (
                event.currentTarget.value !== "" &&
                isValidNumberic(event.currentTarget.value) &&
                parseInt(event.currentTarget.value, 10) <= maxQtyValue
            ) {
                (event.target as any).blur();
            }
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const value = event.currentTarget.value ? Number.parseInt(event.currentTarget.value, 10) : "";

        setProductQty(value.toString());
    };

    const qtyBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const value = event.currentTarget.value ? Number.parseInt(event.currentTarget.value, 10) : 1;

        let qtyToSet = qtyOrdered;
        if (value === minimumOrderQty || value < minimumOrderQty) {
            qtyToSet = minimumOrderQty;
        } else if (multipleSaleQty > 1) {
            const factor = Math.ceil(value / multipleSaleQty);
            qtyToSet = factor * multipleSaleQty;
        } else {
            qtyToSet = value;
        }

        setProductQty(qtyToSet.toString());
        onQtyOrderedChanged(qtyToSet);
    };

    const decreaseByOne = () => {
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const value = Number.parseInt(productQty, 10);

        if (multipleSaleQty > 1) {
            if (value - multipleSaleQty < minimumOrderQty) {
                setProductQty(minimumOrderQty.toString());
                onQtyOrderedChanged(minimumOrderQty);
                return;
            }
            setProductQty((value - multipleSaleQty).toString());
            onQtyOrderedChanged(value - multipleSaleQty);
            return;
        }
        setProductQty((value - 1).toString());
        onQtyOrderedChanged(value - 1);
    };

    const increaseByOne = () => {
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const value = Number.parseInt(productQty, 10);

        if (multipleSaleQty > 1) {
            setProductQty((value + multipleSaleQty).toString());
            onQtyOrderedChanged(value + multipleSaleQty);
            return;
        }

        setProductQty((value + 1).toString());
        onQtyOrderedChanged(value + 1);
    };

    const disabled =
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !productId ||
        !hasEnoughInventory ||
        !canAddToCart ||
        !editable;
    return (
        <StyledWrapper {...styles.wrapper} className={disabled ? "disabled" : ""}>
            {showAddRemoveButtons && (
                <Button onClick={decreaseByOne} sizeVariant="small" disabled={Number(productQty) === 1 || disabled}>
                    <ButtonIcon src="KeyboardArrowDown" size={10} color="secondary.main" />
                </Button>
            )}
            <TextField
                label={labelOverride ?? translate(" ")}
                value={productQty}
                type="text"
                maxLength={maxLengthAttr.qty}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                onBlur={qtyBlurHandler}
                disabled={disabled}
                {...styles.inputStyles}
            />
            {showAddRemoveButtons && (
                <Button
                    onClick={increaseByOne}
                    sizeVariant="small"
                    disabled={Number(productQty) === maxQtyValue || disabled}
                >
                    <ButtonIcon src="KeyboardArrowUp" size={10} color="secondary.main" />
                </Button>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
